import React, {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { Simulation } from 'types/Simulation';
import { StatutPrecarite } from 'types/ResultatVolumeCEE';

type Action =
  | { type: 'ADD_SIMULATION'; payload: LocalSimulation }
  | { type: 'REMOVE_SIMULATION'; payload: string }
  | { type: 'REMOVE_ALL_SIMULATIONS' };

export type LocalSimulation = Simulation & {
  id: string;
  documentsJustificatifsOperation?: string[];
  visiteTechnique?: boolean;
  statutPrecarite?: StatutPrecarite;
};

const simulationsReducer = (
  state: LocalSimulation[],
  action: Action,
): LocalSimulation[] => {
  switch (action.type) {
    // Afin d'éviter les doublons, on ajoute une simulation si elle n'existe pas déjà
    case 'ADD_SIMULATION': {
      const existingSimulation = state.find(
        (sim) => sim.id === action.payload.id,
      );
      if (existingSimulation) {
        return state;
      }
      return [...state, action.payload];
    }
    case 'REMOVE_SIMULATION':
      return state.filter((simulation) => simulation.id !== action.payload);
    case 'REMOVE_ALL_SIMULATIONS':
      return [];
    default:
      return state;
  }
};

interface SimulationsContextProps {
  state: LocalSimulation[];
  dispatch: React.Dispatch<Action>;
  addSimulation: (
    simulation: Simulation,
    documentsJustificatifsOperation?: string[],
    visiteTechnique?: boolean,
    statutPrecarite?: StatutPrecarite,
  ) => void;
  removeSimulation: (simulationId: string) => void;
  removeAllSimulations: (e?: React.MouseEvent) => void;
}

export const SimulationsContext = createContext<
  SimulationsContextProps | undefined
>(undefined);

export const SimulationsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(simulationsReducer, []);

  const removeAllSimulations = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch({ type: 'REMOVE_ALL_SIMULATIONS' });
  }, []);

  const removeSimulation = useCallback((simulationId: string) => {
    dispatch({ type: 'REMOVE_SIMULATION', payload: simulationId });
  }, []);

  const addSimulation = useCallback(
    (
      simulation: LocalSimulation,
      documentsJustificatifsOperation?: string[],
      visiteTechnique?: boolean,
      statutPrecarite?: StatutPrecarite,
    ) => {
      dispatch({
        type: 'ADD_SIMULATION',
        payload: {
          ...simulation,
          documentsJustificatifsOperation,
          visiteTechnique,
          statutPrecarite,
        },
      });
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      addSimulation,
      removeSimulation,
      removeAllSimulations,
    }),
    [state, addSimulation, removeSimulation, removeAllSimulations],
  );

  return (
    <SimulationsContext.Provider value={contextValue}>
      {children}
    </SimulationsContext.Provider>
  );
};

/**
 * Liste les simulations effectuées pendant la session de l'utilisateur
 * @returns Le contexte des simulations
 */
export const useCurrentSimulations = () => {
  return useContext(SimulationsContext)!;
};
